import { useEffect } from 'react';
import { getCmsValue } from './siteContent';
import publicRouteSeo from '../data/publicRouteSeo.json';

const SITE_URL = 'https://harrydabbq.se';
const DEFAULT_IMAGE = '/assets/smokehouse/smoker-process.jpg';
const ORGANIZATION_ID = `${SITE_URL}/#organization`;

const DEFAULT_SEO = {
  title: 'Härryda BBQ – BBQ & catering i Göteborg',
  description: 'Härryda BBQ erbjuder långrökt BBQ, produkter från smokehouse och catering för event i Göteborgsområdet.',
  image: DEFAULT_IMAGE,
  robots: 'index,follow',
  twitterCard: 'summary_large_image',
};

const canonicalUrl = (path = '/') => {
  const parsed = new URL(path || '/', SITE_URL);
  const pathname = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/+$/, '');
  return `${SITE_URL}${pathname}`;
};

const imageUrl = (image) => {
  try {
    const parsed = new URL(image || DEFAULT_IMAGE, SITE_URL);
    return parsed.protocol === 'https:' ? parsed.href : new URL(DEFAULT_IMAGE, SITE_URL).href;
  } catch {
    return new URL(DEFAULT_IMAGE, SITE_URL).href;
  }
};

function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

/** Sätter komplett routespecifik metadata och skapar taggar som saknas. */
export function applySEO({
  title,
  description,
  path = '/',
  robots = DEFAULT_SEO.robots,
  image = DEFAULT_SEO.image,
  twitterCard = DEFAULT_SEO.twitterCard,
} = {}) {
  const resolvedTitle = title || DEFAULT_SEO.title;
  const resolvedDescription = description || DEFAULT_SEO.description;
  const url = canonicalUrl(path);
  const resolvedImage = imageUrl(image);

  document.title = resolvedTitle;
  upsertMeta('name', 'description', resolvedDescription);
  upsertMeta('name', 'robots', robots);
  upsertCanonical(url);
  upsertMeta('property', 'og:title', resolvedTitle);
  upsertMeta('property', 'og:description', resolvedDescription);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:image', resolvedImage);
  upsertMeta('name', 'twitter:card', twitterCard);
  upsertMeta('name', 'twitter:title', resolvedTitle);
  upsertMeta('name', 'twitter:description', resolvedDescription);
  upsertMeta('name', 'twitter:image', resolvedImage);
}

export function useSEO(options = {}) {
  const { title, description, path, robots, image, twitterCard } = options;
  useEffect(() => {
    applySEO({ title, description, path, robots, image, twitterCard });
  }, [title, description, path, robots, image, twitterCard]);
}

/** Injicerar ett routespecifikt JSON-LD-block och städar det vid navigation. */
export function useJsonLd(id, data) {
  const json = data ? JSON.stringify(data) : null;
  // Byggtidens schema (scripts/generate-route-html.js) gäller bara routen det
  // förrenderades för; efter klientnavigering hör det inte till sidan längre.
  // Eget beroende på pathname: runtime-JSON kan vara identisk mellan routes.
  const currentRoute = window.location.pathname.replace(/\/+$/, '') || '/';
  useEffect(() => {
    document.querySelectorAll(
      'script[type="application/ld+json"][data-prerendered-schema="true"]'
    ).forEach((script) => {
      if (script.dataset.route !== currentRoute) script.remove();
    });
  }, [currentRoute]);
  useEffect(() => {
    document.querySelectorAll(
      'script[type="application/ld+json"][data-generated-route-schema="true"]'
    ).forEach((script) => script.remove());
    document.getElementById(id)?.remove();
    if (!json) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.textContent = json;
    document.head.appendChild(script);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [id, json]);
}

const present = (value) => typeof value === 'string' && value.trim().length > 0;

const validEmail = (value) => present(value) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const validHttpsUrl = (value) => {
  if (!present(value)) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const postalAddress = (streetAddress, postalCode, addressLocality) => {
  const address = {
    '@type': 'PostalAddress',
    ...(present(streetAddress) ? { streetAddress: streetAddress.trim() } : {}),
    ...(present(postalCode) ? { postalCode: postalCode.trim() } : {}),
    ...(present(addressLocality) ? { addressLocality: addressLocality.trim() } : {}),
  };
  return Object.keys(address).length > 1 ? { ...address, addressCountry: 'SE' } : null;
};

const placeFromCms = (cms, prefix, id) => {
  const name = cms(`${prefix}_name`);
  const description = cms(`${prefix}_description`);
  const address = postalAddress(
    cms(`${prefix}_street_address`),
    cms(`${prefix}_postal_code`),
    cms(`${prefix}_locality`)
  );
  if (!present(name) && !present(description) && !address) return null;
  return {
    '@type': 'Place',
    ...(id ? { '@id': id } : {}),
    ...(present(name) ? { name: name.trim() } : {}),
    ...(present(description) ? { description: description.trim() } : {}),
    ...(address ? { address } : {}),
  };
};

const BREADCRUMBS = {
  '/': [],
  '/kontakt': ['Kontakt'],
  '/produkter': ['Produkter'],
  '/catering': ['Catering'],
  '/pulled-pork': ['Pulled pork'],
  '/brisket': ['Brisket'],
};

const breadcrumbList = (path) => {
  const parsedPath = new URL(path || '/', SITE_URL).pathname.replace(/\/+$/, '') || '/';
  const labels = BREADCRUMBS[parsedPath] || [];
  const items = [{ name: 'Hem', item: SITE_URL }];
  labels.forEach((name) => items.push({ name, item: canonicalUrl(parsedPath) }));
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      ...item,
    })),
  };
};

/** Bygger sanningsenlig structured data från samma CMS-identitet som publika kontaktvägar. */
export function buildPublicStructuredData(siteContent, path = '/') {
  const cms = (field) => getCmsValue(siteContent, 'business_identity', field);
  const phone = cms('phone');
  const email = cms('email');
  const sameAs = ['instagram_url', 'facebook_url', 'tiktok_url', 'youtube_url']
    .map(cms)
    .filter(validHttpsUrl);
  const smokehouse = placeFromCms(cms, 'smokehouse', `${SITE_URL}/#smokehouse`);
  const vassen = placeFromCms(cms, 'vassen', `${SITE_URL}/#vassen-market`);
  const organization = {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'Härryda BBQ',
    url: SITE_URL,
    logo: `${SITE_URL}/assets/smokehouse/harryda-bbq-logo-400.webp`,
    ...(present(phone) ? { telephone: phone.trim() } : {}),
    ...(validEmail(email) ? { email: email.trim() } : {}),
    ...(sameAs.length ? { sameAs } : {}),
    ...(smokehouse ? { location: smokehouse } : {}),
  };
  const parsedPath = new URL(path || '/', SITE_URL).pathname.replace(/\/+$/, '') || '/';
  const graph = [organization];
  if (vassen) graph.push(vassen);
  if (parsedPath === '/catering') {
    graph.push({
      '@type': 'Service',
      '@id': `${SITE_URL}/catering#service`,
      serviceType: 'BBQ-catering',
      name: 'BBQ-catering från Härryda BBQ',
      url: `${SITE_URL}/catering`,
      provider: { '@id': ORGANIZATION_ID },
    });
  }
  graph.push(breadcrumbList(parsedPath));
  return { '@context': 'https://schema.org', '@graph': graph };
}

export const FAQ_SLOT_COUNT = 6;

/**
 * Läser en sektions FAQ ur CMS:et. Tomma slots hoppas över, så en fråga kan
 * tas bort genom att fältet rensas i admin utan att koden behöver ändras.
 */
export function buildSectionFaq(siteContent, section) {
  const cms = (field) => getCmsValue(siteContent, section, field);
  return Array.from({ length: FAQ_SLOT_COUNT }, (_, index) => ({
    slot: index + 1,
    question: cms(`faq_${index + 1}_question`),
    answer: cms(`faq_${index + 1}_answer`),
  })).filter(({ question, answer }) => present(question) && present(answer));
}

/** Cateringsidans FAQ (sektionen catering_seo). */
export function buildCateringFaq(siteContent) {
  return buildSectionFaq(siteContent, 'catering_seo');
}

/** FAQPage-schema byggt ur samma fält som renderas, så schema och sidtext matchar. */
export function buildFaqSchema(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question.trim(),
      acceptedAnswer: { '@type': 'Answer', text: answer.trim() },
    })),
  };
}

export const PAGE_SEO = {
  '/': DEFAULT_SEO,
  '/catering': {
    title: 'BBQ-catering i Göteborg – prisbild & förfrågan | Härryda BBQ',
    description: 'Bygg en prisbild för BBQ-catering till företagsevent och stora fester i Göteborgsområdet och skicka en cateringförfrågan.',
    image: DEFAULT_IMAGE,
  },
  '/produkter': {
    ...publicRouteSeo['/produkter'],
    image: DEFAULT_IMAGE,
  },
  '/pulled-pork': {
    ...publicRouteSeo['/pulled-pork'],
    image: DEFAULT_IMAGE,
  },
  '/brisket': {
    ...publicRouteSeo['/brisket'],
    image: DEFAULT_IMAGE,
  },
  '/kontakt': {
    ...publicRouteSeo['/kontakt'],
    image: DEFAULT_IMAGE,
  },
  '/butiker': {
    ...publicRouteSeo['/butiker'],
    image: DEFAULT_IMAGE,
  },
  '/shop': {
    title: 'Shop – merch från Härryda BBQ',
    description: 'Se tillgänglig merch från Härryda BBQ.',
    image: DEFAULT_IMAGE,
  },
  '/order': {
    ...publicRouteSeo['/order'],
    image: DEFAULT_IMAGE,
  },
};
